import React from 'react';
import { format, parseISO } from 'date-fns';
import { PencilIcon } from 'lucide-react';
import { BookingForm } from '../../hooks/useBookingForm';
import { formatNaira, pluralize } from '../../utils/format';
import { formatTime12 } from '../../utils/time';

/** Read-only review of everything the guest entered, with links back to each step. */
export function ReviewSummary({
  form
}: {
  form: BookingForm;
}) {
  const {
    isStay,
    property,
    checkIn,
    checkOut,
    shootDate,
    startTime,
    endTime,
    guests,
    guest,
    estimate
  } = form;
  const groups = [{
    step: 1,
    title: 'Booking',
    rows: [['Type', isStay ? 'Apartment Stay' : 'Photoshoot'], ...(isStay ? [['Apartment', `${property.name} (${property.id})`]] : [])]
  }, {
    step: 2,
    title: 'Details',
    rows: isStay ? [['Check-in', checkIn ? `${format(parseISO(checkIn), 'EEE d MMM yyyy')} · 2:00 PM` : '—'], ['Check-out', checkOut ? `${format(parseISO(checkOut), 'EEE d MMM yyyy')} · 12:00 noon` : '—'], ['Nights', String(estimate.nights)], ['Guests', pluralize(guests, 'guest')]] : [['Date', shootDate ? format(parseISO(shootDate), 'EEEE d MMMM yyyy') : '—'], ['Time', startTime && endTime ? `${formatTime12(startTime)} – ${formatTime12(endTime)}` : '—'], ['People on set', String(guests)]]
  }, {
    step: 3,
    title: 'Your information',
    rows: [['Full name', guest.fullName], ['Email', guest.email], ['Phone', guest.phone], ['Special requests', guest.specialRequests || 'None']]
  }];
  return <div className="space-y-6" data-id="element-218" data-mp="194h8yiqqukwn">
      {groups.map(group => <div key={group.title} className="rounded-sm bg-ivory-100 p-5" data-id="element-219" data-mp="1973d08efeia4">
          <div className="mb-3 flex items-center justify-between" data-id="element-220" data-mp="3ps2yp4buv3ll">
            <p className="text-[11px] uppercase tracking-overline text-gold-700" data-id="element-221" data-mp="3tvhq0r58v6ec">{group.title}</p>
            <button type="button" onClick={() => form.goTo(group.step)} className="inline-flex items-center gap-1.5 text-[12px] text-forest-600 transition-colors duration-150 hover:text-forest-800" data-id="element-222" data-mp="20cs92zplm8s9">
              <PencilIcon size={12} aria-hidden="true" data-id="element-223" data-mp="1qkmh3pzcjrd2" />
              Edit
            </button>
          </div>
          <dl className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-224" data-mp="1llryao1dq2sy">
            {group.rows.map(([label, value]) => <div key={label} className={label === 'Special requests' ? 'sm:col-span-2' : ''} data-id="element-225" data-mp="1cn7rol93fjl3">
                <dt className="text-[12px] text-muted" data-id="element-226" data-mp="17nl8zwahxb4w">{label}</dt>
                <dd className="break-words text-[14px] text-ink" data-id="element-227" data-mp="17pxs4crzprjj">{value}</dd>
              </div>)}
          </dl>
        </div>)}

      <div className="rounded-sm bg-forest-700 p-5 text-ivory-100" data-id="element-228" data-mp="3224em7qc3ur5">
        <p className="text-[11px] uppercase tracking-overline text-gold-300" data-id="element-229" data-mp="2x2rsxqb78tq5">Charges (estimate)</p>
        <ul className="mt-3 space-y-2.5" data-id="element-230" data-mp="35js3pujz8cvz">
          {estimate.lines.map(line => <li key={line.label} className="flex items-start justify-between gap-4 text-[14px]" data-id="element-231" data-mp="35hpcx8b02fq7">
              <span className="text-ivory-200/80" data-id="element-232" data-mp="39jaqywz9t01z">{line.label}</span>
              <span className={line.conditional ? 'text-right text-[12px] text-gold-300' : ''} data-id="element-233" data-mp="3edez3jk9ve4n">
                {line.conditional ?? formatNaira(line.amount)}
              </span>
            </li>)}
        </ul>
        <div className="mt-4 flex items-baseline justify-between border-t border-ivory-100/15 pt-4" data-id="element-234" data-mp="2mmqz4auc4esk">
          <span className="text-[11px] uppercase tracking-overline text-gold-300" data-id="element-235" data-mp="2qolwx5d24a1l">Estimated total</span>
          <span className="font-display text-2xl" data-id="element-236" data-mp="2vnp3wvclkqc7">{formatNaira(estimate.total)}</span>
        </div>
        <p className="mt-3 text-[12px] leading-relaxed text-ivory-200/70" data-id="element-237" data-mp="30im9fsao4hkc">
          Final amounts — including any approved early check-in or late checkout fees — are calculated by our booking system.
        </p>
      </div>
    </div>;
}